import React, { useRef, useState } from 'react';
import { Download, Copy, Check, Image as ImageIcon, Square, Share2 } from 'lucide-react';
import * as htmlToImage from 'html-to-image';

/**
 * Stunning Facebook / Instagram ad creative for the Measured Livestock
 * NZ-wide website promo. Designed to be screenshot-perfect and one-click
 * downloadable as PNG. Two formats included:
 *   1) Facebook Feed:   1200 × 630  (recommended ratio 1.91:1)
 *   2) Instagram / FB Square: 1080 × 1080
 */
const FacebookAdNZ = () => {
  const feedRef = useRef(null);
  const squareRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const handleDownload = async (ref, filename) => {
    if (!ref.current) return;
    setDownloading(filename);
    try {
      const dataUrl = await htmlToImage.toPng(ref.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#1a4d2e',
      });
      const link = document.createElement('a');
      link.download = filename;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Download failed:', err);
      alert('Could not generate the PNG. Try a different browser if it persists.');
    } finally {
      setDownloading(null);
    }
  };

  const adCopy = `Every Animal Checked & Verified.

Measured Livestock — the trusted NZ-wide trait collection service for dairy and beef farms.

✓ Liveweight, DNA, Herd Read & Body Condition Score
✓ One visit. One price. Clean data straight back to your records.
✓ See your job priced instantly on our PriceHub

📍 Servicing all of New Zealand
👉 Visit measuredlivestock.com — get your quote in 2 minutes.

#NZFarming #DairyNZ #BeefNZ #HerdData #MeasuredLivestock`;

  const handleCopy = () => {
    navigator.clipboard.writeText(adCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Inline background image — using a CDN pasture image already used on the landing hero
  const heroImg = 'https://images.unsplash.com/photo-1709733971576-8caa9df3abf9?w=1600&q=80';

  return (
    <section className="bg-gradient-to-br from-[#1a4d2e] via-[#2d6b47] to-[#1a4d2e] py-12">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-8">
          <span className="inline-block bg-[#ff6b00] text-white px-4 py-1 rounded-full text-xs uppercase tracking-widest font-bold mb-3">
            New · National NZ Wide
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-2">
            Website Promo — Facebook &amp; Instagram
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto">
            Pixel-perfect ad creative ready for Facebook Feed and Instagram. Download as PNG and post directly.
          </p>
        </div>

        {/* Two ad previews side-by-side */}
        <div className="grid lg:grid-cols-2 gap-8 items-start mb-8">
          {/* === FACEBOOK FEED 1200x630 === */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-white/90 text-sm font-semibold flex items-center gap-2">
                <ImageIcon className="w-4 h-4" /> Facebook Feed · 1200 × 630
              </span>
              <button
                onClick={() => handleDownload(feedRef, 'measuredlivestock-nz-ad-1200x630.png')}
                disabled={downloading === 'measuredlivestock-nz-ad-1200x630.png'}
                className="flex items-center gap-1.5 bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white text-sm font-semibold px-3 py-1.5 rounded transition-all disabled:opacity-60"
                data-testid="download-fb-feed-btn"
              >
                <Download className="w-4 h-4" />
                {downloading === 'measuredlivestock-nz-ad-1200x630.png' ? 'Generating…' : 'Download PNG'}
              </button>
            </div>

            {/* The actual creative — fixed pixel dimensions, scaled for preview */}
            <div className="bg-white rounded-lg shadow-2xl p-2 overflow-hidden">
              <div
                className="origin-top-left"
                style={{
                  transform: 'scale(0.5)',
                  width: 1200,
                  height: 630,
                  marginRight: -600,
                  marginBottom: -315,
                }}
              >
                <div
                  ref={feedRef}
                  style={{
                    width: 1200,
                    height: 630,
                    position: 'relative',
                    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                    overflow: 'hidden',
                    backgroundImage: `linear-gradient(110deg, rgba(26,77,46,0.96) 0%, rgba(26,77,46,0.92) 38%, rgba(26,77,46,0.55) 70%, rgba(26,77,46,0.15) 100%), url(${heroImg})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  {/* Brand strip */}
                  <div style={{ position: 'absolute', top: 36, left: 56, display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ width: 6, height: 36, background: '#ff6b00' }} />
                    <span style={{
                      color: 'white', fontSize: 16, letterSpacing: 6, textTransform: 'uppercase', fontWeight: 700, opacity: 0.92,
                    }}>
                      Measured Livestock
                    </span>
                  </div>

                  {/* NZ-wide pill */}
                  <div style={{
                    position: 'absolute', top: 36, right: 56,
                    background: '#ff6b00', color: 'white', padding: '8px 18px',
                    borderRadius: 999, fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase',
                  }}>
                    🇳🇿 Servicing all of NZ
                  </div>

                  {/* Headline */}
                  <div style={{ position: 'absolute', top: 130, left: 56, right: 480, color: 'white' }}>
                    <div style={{ fontSize: 38, fontWeight: 600, opacity: 0.95, marginBottom: 6 }}>
                      Trait Collection Service
                    </div>
                    <h1 style={{
                      fontSize: 78, lineHeight: 1.02, fontWeight: 900, margin: 0,
                      letterSpacing: -1.5, textShadow: '0 4px 30px rgba(0,0,0,0.35)',
                    }}>
                      Every Animal
                    </h1>
                    <h1 style={{
                      fontSize: 78, lineHeight: 1.02, fontWeight: 900, margin: '0 0 14px 0',
                      color: '#ff8533', letterSpacing: -1.5, textShadow: '0 4px 30px rgba(0,0,0,0.45)',
                    }}>
                      Checked &amp; Verified
                    </h1>
                    <div style={{ fontSize: 24, fontWeight: 500, opacity: 0.95 }}>
                      Liveweight · DNA · Herd Read · Body Condition Score
                    </div>
                  </div>

                  {/* CTA card bottom-left */}
                  <div style={{
                    position: 'absolute', bottom: 36, left: 56, right: 56,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 24,
                  }}>
                    <div style={{ color: 'white' }}>
                      <div style={{ fontSize: 16, letterSpacing: 3, textTransform: 'uppercase', opacity: 0.8, marginBottom: 4 }}>
                        See your price instantly
                      </div>
                      <div style={{ fontSize: 42, fontWeight: 800, letterSpacing: -0.5 }}>
                        measuredlivestock.com
                      </div>
                    </div>
                    <div style={{
                      background: '#ff6b00', color: 'white', padding: '20px 38px', borderRadius: 8,
                      fontSize: 24, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
                      boxShadow: '0 10px 28px rgba(0,0,0,0.35)',
                    }}>
                      Get Your Quote →
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* === INSTAGRAM SQUARE 1080x1080 === */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-white/90 text-sm font-semibold flex items-center gap-2">
                <Square className="w-4 h-4" /> Instagram / FB Square · 1080 × 1080
              </span>
              <button
                onClick={() => handleDownload(squareRef, 'measuredlivestock-nz-ad-1080x1080.png')}
                disabled={downloading === 'measuredlivestock-nz-ad-1080x1080.png'}
                className="flex items-center gap-1.5 bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white text-sm font-semibold px-3 py-1.5 rounded transition-all disabled:opacity-60"
                data-testid="download-fb-square-btn"
              >
                <Download className="w-4 h-4" />
                {downloading === 'measuredlivestock-nz-ad-1080x1080.png' ? 'Generating…' : 'Download PNG'}
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-2xl p-2 overflow-hidden">
              <div
                className="origin-top-left"
                style={{
                  transform: 'scale(0.5)',
                  width: 1080,
                  height: 1080,
                  marginRight: -540,
                  marginBottom: -540,
                }}
              >
                <div
                  ref={squareRef}
                  style={{
                    width: 1080,
                    height: 1080,
                    position: 'relative',
                    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                    overflow: 'hidden',
                    backgroundImage: `linear-gradient(180deg, rgba(26,77,46,0.92) 0%, rgba(26,77,46,0.55) 45%, rgba(26,77,46,0.95) 100%), url(${heroImg})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  {/* Top: brand + NZ */}
                  <div style={{
                    position: 'absolute', top: 56, left: 56, right: 56,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      <div style={{ width: 6, height: 36, background: '#ff6b00' }} />
                      <span style={{
                        color: 'white', fontSize: 18, letterSpacing: 6, textTransform: 'uppercase', fontWeight: 700,
                      }}>
                        Measured Livestock
                      </span>
                    </div>
                    <div style={{
                      background: '#ff6b00', color: 'white', padding: '10px 22px',
                      borderRadius: 999, fontSize: 15, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase',
                    }}>
                      🇳🇿 NZ Wide
                    </div>
                  </div>

                  {/* Centre — big headline */}
                  <div style={{
                    position: 'absolute', top: '50%', left: 56, right: 56,
                    transform: 'translateY(-58%)', color: 'white', textAlign: 'center',
                  }}>
                    <div style={{ fontSize: 30, letterSpacing: 4, textTransform: 'uppercase', opacity: 0.9, marginBottom: 12, fontWeight: 600 }}>
                      Trait Collection Service
                    </div>
                    <h1 style={{
                      fontSize: 96, lineHeight: 1.02, fontWeight: 900, margin: 0,
                      letterSpacing: -2, textShadow: '0 6px 36px rgba(0,0,0,0.4)',
                    }}>
                      Every Animal
                    </h1>
                    <h1 style={{
                      fontSize: 96, lineHeight: 1.02, fontWeight: 900, margin: '6px 0 26px',
                      color: '#ff8533', letterSpacing: -2, textShadow: '0 6px 36px rgba(0,0,0,0.45)',
                    }}>
                      Checked &amp; Verified
                    </h1>
                    <div style={{ fontSize: 32, fontWeight: 500, opacity: 0.96, marginBottom: 22, letterSpacing: 0.5 }}>
                      Liveweight · DNA · Herd Read · BCS
                    </div>
                    <div style={{ fontSize: 22, opacity: 0.85, maxWidth: 800, margin: '0 auto', lineHeight: 1.4 }}>
                      Trusted on-farm field teams. One visit. One price. Clean data straight back to your records.
                    </div>
                  </div>

                  {/* Bottom CTA */}
                  <div style={{
                    position: 'absolute', bottom: 56, left: 56, right: 56, textAlign: 'center',
                  }}>
                    <div style={{
                      background: '#ff6b00', color: 'white', padding: '22px 0', borderRadius: 10,
                      fontSize: 36, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
                      boxShadow: '0 12px 30px rgba(0,0,0,0.4)',
                    }}>
                      measuredlivestock.com
                    </div>
                    <div style={{
                      color: 'white', marginTop: 14, fontSize: 18, letterSpacing: 3, textTransform: 'uppercase', opacity: 0.85,
                    }}>
                      Get your quote in 2 minutes
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ad copy block */}
        <div className="bg-white rounded-xl p-6 shadow-2xl">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="font-bold text-[#1a4d2e] flex items-center gap-2 text-lg">
              <Share2 className="w-5 h-5 text-[#ff6b00]" />
              Ready-to-Post Ad Copy
            </h3>
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 bg-[#1a4d2e] hover:bg-[#1a4d2e]/90 text-white px-4 py-2 rounded-lg text-sm font-semibold"
              data-testid="copy-fb-ad-copy-btn"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy Caption'}
            </button>
          </div>
          <pre className="whitespace-pre-wrap text-sm text-[#4a4a4a] bg-[#f7f5f1] border border-[#e5ddd5] p-4 rounded-lg leading-relaxed font-sans">
{adCopy}
          </pre>
          <p className="text-xs text-[#8d6e63] mt-3">
            💡 <strong>Targeting tip:</strong> Set Facebook Ads location to <strong>New Zealand</strong>, interests:
            <em> Dairy farming · Beef cattle · Livestock · Farm management · NAIT</em>. Recommended budget:
            $5–$10/day for 7 days to gauge response.
          </p>
        </div>
      </div>
    </section>
  );
};

export default FacebookAdNZ;
